// src/pages/WindsorTaxi.jsx
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import PostcodeQuotenew from "./PostcodeQuotenew";
import LiveStrip from "../components/LiveStrip";
import FloatingSupportChat from "../components/FloatingSupportChat";
import PremiumContactStrip from "../components/PremiumContactStrip";
import CcgPremiumReviews from "../components/CcgPremiumReviews";
import CcgRouteLhrStatusCompact from "../components/CcgRouteLhrStatusCompact";
import "./CcgRoutePremium.css";
import "../styles/windsor-taxi.css";

const seoTitle = "Windsor Taxi & Fixed-Price Heathrow Transfers | CheapCabGrab";
const seoDescription = "Book reliable Windsor taxis for Heathrow, Gatwick, Luton, Stansted and local SL4 journeys. Fixed-price fares, licensed drivers, MPV & minibus options, 24/7 support.";
const seoCanonical = "https://www.cheapcabgrab.com/windsor-taxi";
const seoKeywords = "Windsor taxi, SL4 taxi, Windsor to Heathrow taxi, Heathrow transfers from Windsor, fixed price taxi Windsor, airport taxi Windsor, CheapCabGrab";
const ogTitle = "Windsor Taxi – Fixed-Price Heathrow Transfers & Local SL4 Cabs";
const ogDescription = "Book a CheapCabGrab taxi from Windsor & Eton to Heathrow and all major London airports. Fixed fares, no surge pricing, licensed drivers and airport specialists.";

const windsorTaxiJsonLd = {
  "@context": "https://schema.org",
  "@type": "TaxiService",
  name: "CheapCabGrab – Windsor Taxi Service",
  url: seoCanonical,
  areaServed: ["Windsor SL4", "Eton", "Old Windsor", "Datchet", "Bray", "Maidenhead", "Cookham", "Ascot"],
  serviceArea: { "@type": "Place", name: "Windsor, Eton and Heathrow corridor" },
  sameAs: ["https://www.cheapcabgrab.com/", "https://www.cheapcabgrab.com/heathrow-taxi"],
};

const windsorFaqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Can you collect from Windsor Castle area?", acceptedAnswer: { "@type": "Answer", text: "Yes – pickups around Windsor Castle, town centre hotels and the Long Walk area are common. We’ll confirm the best pickup point when you book." } },
    { "@type": "Question", name: "Do you offer taxis from Windsor to Heathrow at night?", acceptedAnswer: { "@type": "Answer", text: "Yes — CheapCabGrab runs 24/7, including early-morning flights and late-night collections from all Heathrow terminals." } },
    { "@type": "Question", name: "How long is a taxi from Windsor to Heathrow?", acceptedAnswer: { "@type": "Answer", text: "Typically 15–30 minutes depending on route and traffic around the A308/A332 or Datchet Road." } },
    { "@type": "Question", name: "Do you provide larger minibuses for Windsor groups?", acceptedAnswer: { "@type": "Answer", text: "Yes — MPV, 7-seater, 9-seater and 14-seater options are available. For large groups, see our minibus and coach hire routes." } },
  ],
};

const WindsorTaxi = () => {
  const scrollToForm = () => {
    const el = document.getElementById("windsor-quote-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const setMeta = (selector, attrs) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        document.head.appendChild(element);
      }
      Object.entries(attrs).forEach(([key, value]) => element.setAttribute(key, value));
    };
    const setJsonLd = (id, data) => {
      let script = document.querySelector(`script[data-windsor-taxi-jsonld="${id}"]`);
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-windsor-taxi-jsonld", id);
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };
    const applySeo = () => {
      document.title = seoTitle;
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = seoCanonical;
      setMeta('meta[name="description"]', { name: "description", content: seoDescription });
      setMeta('meta[name="keywords"]', { name: "keywords", content: seoKeywords });
      setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
      setMeta('meta[property="og:title"]', { property: "og:title", content: ogTitle });
      setMeta('meta[property="og:description"]', { property: "og:description", content: ogDescription });
      setMeta('meta[property="og:url"]', { property: "og:url", content: seoCanonical });
      setMeta('meta[property="og:image"]', { property: "og:image", content: "https://www.cheapcabgrab.com/og-image.jpg" });
      setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
      setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: seoTitle });
      setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: seoDescription });
      setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: "https://www.cheapcabgrab.com/og-image.jpg" });
      setJsonLd("taxi-service", windsorTaxiJsonLd);
      setJsonLd("faq", windsorFaqJsonLd);
    };
    applySeo();
    const timers = [window.setTimeout(applySeo, 0), window.setTimeout(applySeo, 750)];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  return (
    <div className="windsor-page route-page ccg-route-premium ccg-windsor-premium">
      <Helmet>
        <title>{seoTitle}</title>
        <meta
          name="description"
          content="Book reliable Windsor taxis for Heathrow, Gatwick, Luton, Stansted and local SL4 journeys. Fixed-price fares, licensed drivers, MPV & minibus options, 24/7 support."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/windsor-taxi" />
        <meta
          name="keywords"
          content={seoKeywords}
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={ogTitle}
        />
        <meta
          property="og:description"
          content={ogDescription}
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/windsor-taxi" />
        <meta property="og:image" content="https://www.cheapcabgrab.com/og-image.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://www.cheapcabgrab.com/og-image.jpg" />
        <script type="application/ld+json" data-windsor-taxi-jsonld="taxi-service">{JSON.stringify(windsorTaxiJsonLd)}</script>
        <script type="application/ld+json" data-windsor-taxi-jsonld="faq">{JSON.stringify(windsorFaqJsonLd)}</script>
      </Helmet>

      <LiveStrip variant="windsor" />

      <main className="windsor-main route-main">
        {/* 🟦 HERO */}
        <section className="ccg-route-hero ccg-windsor-hero">
          <div className="ccg-route-hero-shell">
            <div className="ccg-route-hero-top">
              <div className="ccg-route-hero-copy">
                <span className="ccg-route-eyebrow">Windsor taxi service</span>
                <h1>Windsor Taxi – Heathrow &amp; SL4 Transfers</h1>
                <p>Windsor, Eton, Datchet &amp; Old Windsor specialists</p>
                <p>
                  Book reliable taxis from Windsor and Eton to Heathrow, London and major airports.
                  Fixed upfront fares, licensed drivers, meet &amp; greet and luggage-ready vehicles.
                </p>
              </div>
              <div className="ccg-route-trust-cards" aria-label="CheapCabGrab Windsor taxi trust signals">
                <article className="ccg-route-trust-card"><b>✓</b><span><small>Licensed operator</small><strong>Licensed &amp; insured</strong><span>Professional airport transfers</span></span></article>
                <article className="ccg-route-trust-card"><b>G</b><span><small>Google reviews</small><strong>4.8 ★★★★★</strong><span>Trusted by local passengers</span></span></article>
              </div>
            </div>

            <div className="ccg-route-quote-wrap" id="windsor-quote-form">
              <div className="ccg-route-quote-head">
                <span className="ccg-route-quote-label">Get instant Windsor taxi quotes</span>
                <span className="ccg-route-quote-note"><i /> Free - Fast - No obligation</span>
              </div>
              <PostcodeQuotenew presetPickup="Windsor Castle, Windsor SL4" />
              <ul className="ccg-route-quote-promises"><li>Fixed upfront fare</li><li>Licensed drivers</li><li>Secure online booking</li><li>Instant confirmation</li></ul>
              <PremiumContactStrip />
            </div>

            <ul className="ccg-route-flags">
              <li>Heathrow, Gatwick, Luton &amp; Stansted coverage</li>
              <li>Fixed prices — no surge pricing</li>
              <li>Meet &amp; greet, flight tracking, licensed drivers</li>
              <li>Saloon, MPV, 8–16 seat minibus &amp; coach options</li>
            </ul>

            <div className="ccg-route-review-strip">
              <CcgPremiumReviews pageKey="/windsor-taxi" ariaLabel="Windsor taxi customer reviews" />
            </div>
          </div>
        </section>

        <CcgRouteLhrStatusCompact title="Live Heathrow checks for Windsor airport taxis" />

        {/* FLOATING CTA */}
        <button className="route-floating-book-btn" onClick={scrollToForm}>
          Book Windsor Taxi
        </button>

        {/* WINDSOR AT A GLANCE */}
        <section className="windsor-section" id="windsor-areas">
          <header className="windsor-section-header">
            <h2>🏙️ Windsor at a Glance (SL4 & Nearby)</h2>
            <p>
              CheapCabGrab covers Windsor, Eton and the wider SL4 corridor. Here’s how we support
              the key local areas.
            </p>
          </header>
          <div className="windsor-two-col">
            <div className="windsor-info-card">
              <h3>Windsor Town Centre (SL4)</h3>
              <ul>
                <li>🏰 Windsor Castle, Long Walk & town centre hotels</li>
                <li>🚶 Busy pedestrian areas and narrow streets</li>
                <li>🚕 Frequent taxis to Heathrow, London & Legoland</li>
              </ul>
            </div>
            <div className="windsor-info-card">
              <h3>Eton & Eton College</h3>
              <ul>
                <li>📍 Just across the River Thames from Windsor</li>
                <li>🏫 School visits, events and term-time travel</li>
                <li>🚖 Regular taxis to Windsor, Heathrow & London</li>
              </ul>
            </div>
            <div className="windsor-info-card">
              <h3>Old Windsor & Datchet</h3>
              <ul>
                <li>🏡 Riverside villages south and east of Windsor</li>
                <li>🚗 Quick access to the M25 and Heathrow</li>
                <li>🛫 Ideal for early morning airport transfers</li>
              </ul>
            </div>
            <div className="windsor-info-card">
              <h3>Bray, Maidenhead & Cookham</h3>
              <ul>
                <li>🍽️ Fine-dining restaurants and riverside pubs</li>
                <li>🚆 Good rail links combined with taxi transfers</li>
                <li>🚌 Minibuses for group dinners and events</li>
              </ul>
            </div>
          </div>
        </section>

        {/* VEHICLES SECTION – LINK TO /fleet */}
        <section className="windsor-section" id="windsor-vehicles">
          <header className="windsor-section-header">
            <h2>🚘 Our Windsor Taxi Vehicles</h2>
            <p>
              Whether you are heading to Heathrow, central London or a countryside restaurant, our
              vehicle range covers solo travellers, families and larger groups.
            </p>
          </header>
          <div className="windsor-vehicle-grid">
            <article className="windsor-vehicle-card">
              <h3>Saloon</h3>
              <p>Perfect for individuals or couples travelling light from Windsor to Heathrow.</p>
              <ul>
                <li>👥 Up to 3 passengers</li>
                <li>🧳 2–3 medium suitcases</li>
                <li>🛫 Typical use: Windsor ↔ Heathrow or Windsor ↔ London</li>
              </ul>
            </article>
            <article className="windsor-vehicle-card">
              <h3>Estate / MPV</h3>
              <p>
                Extra luggage space for families staying longer or carrying pushchairs and extra
                bags.
              </p>
              <ul>
                <li>👥 Up to 4 passengers</li>
                <li>🧳 Larger boot space</li>
                <li>🏨 Great for hotel & apartment transfers</li>
              </ul>
            </article>
            <article className="windsor-vehicle-card">
              <h3>7–9 Seater Minibus</h3>
              <p>
                Ideal for family groups and small tours of Windsor, Legoland, London and nearby
                attractions.
              </p>
              <ul>
                <li>👥 Up to 7–9 passengers</li>
                <li>🧳 Plenty of space for luggage and buggies</li>
                <li>🚌 Popular for day trips and events</li>
              </ul>
            </article>
            <article className="windsor-vehicle-card">
              <h3>14-Seater & Coach</h3>
              <p>
                For bigger groups, weddings, corporate outings and school trips around Windsor and
                Heathrow.
              </p>
              <ul>
                <li>👥 14+ passengers</li>
                <li>📅 Pre-book only</li>
                <li>
                  🤝 Operated in partnership with our PSV partner RHP Minibus Hire UK Limited for
                  coach services.
                </li>
              </ul>
            </article>
          </div>
          <p className="windsor-fleet-link">
            See full vehicle details in our <Link to="/fleet">CheapCabGrab fleet guide</Link>.
          </p>
        </section>

        {/* TYPICAL FARES */}
        <section className="windsor-section" id="windsor-fares">
          <header className="windsor-section-header">
            <h2>💷 Typical Windsor Taxi Fares & Journey Times</h2>
            <p>
              These are guide prices for standard saloon vehicles. Your exact fixed fare will depend
              on pickup point, time and vehicle type – always shown clearly before you confirm.
            </p>
          </header>
          <div className="windsor-fares-grid">
            <article className="windsor-fare-card">
              <h3>Windsor → Heathrow</h3>
              <ul>
                <li>📍 Around 8–12 miles (depending on route)</li>
                <li>⏱ 15–30 minutes</li>
                <li>💷 From <strong>£40–£55</strong></li>
              </ul>
            </article>
            <article className="windsor-fare-card">
              <h3>Windsor → Gatwick</h3>
              <ul>
                <li>📍 Around 45–55 miles</li>
                <li>⏱ 50–80 minutes</li>
                <li>💷 From <strong>£95–£120</strong></li>
              </ul>
            </article>
            <article className="windsor-fare-card">
              <h3>Windsor → Luton</h3>
              <ul>
                <li>📍 Around 35–40 miles</li>
                <li>⏱ 40–70 minutes</li>
                <li>💷 From <strong>£75–£95</strong></li>
              </ul>
            </article>
            <article className="windsor-fare-card">
              <h3>Windsor → Stansted</h3>
              <ul>
                <li>📍 Around 60–70 miles</li>
                <li>⏱ 80–110 minutes</li>
                <li>💷 From <strong>£125–£160</strong></li>
              </ul>
            </article>
          </div>
          <p className="windsor-fares-note">
            For live pricing, please use the quote form above or start a new search on the{' '}
            <Link to="/">CheapCabGrab homepage</Link>.
          </p>
        </section>

        {/* ATTRACTIONS NEAR WINDSOR */}
        <section className="windsor-section" id="windsor-attractions">
          <header className="windsor-section-header">
            <h2>🏰 Attractions Near Windsor</h2>
            <p>
              Windsor is packed with heritage sites, green spaces and family attractions. CheapCabGrab
              runs taxis and minibuses to the main highlights.
            </p>
          </header>
          <div className="windsor-attractions-grid">
            <article className="windsor-attraction-card">
              <h3>Windsor Castle & Long Walk</h3>
              <ul>
                <li>📍 Right in Windsor town centre</li>
                <li>💷 Local short-hop fares to hotels & stations</li>
                <li>🚶 Perfect combined with walking tours</li>
              </ul>
            </article>
            <article className="windsor-attraction-card">
              <h3>Legoland Windsor Resort</h3>
              <ul>
                <li>📍 Around 3–4 miles from Windsor centre</li>
                <li>⏱ 10–15 minutes by taxi</li>
                <li>💷 Typical fare: £15–£25 (saloon)</li>
              </ul>
            </article>
            <article className="windsor-attraction-card">
              <h3>Central London & West End</h3>
              <ul>
                <li>📍 20–25 miles from Windsor</li>
                <li>⏱ 45–80 minutes (traffic)</li>
                <li>💷 Typical fare: £80–£110 (saloon)</li>
              </ul>
            </article>
          </div>
          <p className="windsor-attractions-link">
            For more ideas around Heathrow and Windsor, see our{' '}
            <Link to="/heathrow-attractions">Heathrow & Windsor attractions guide</Link>.
          </p>
        </section>

        {/* HOTELS NEAR WINDSOR / HEATHROW */}
        <section className="windsor-section" id="windsor-hotels">
          <header className="windsor-section-header">
            <h2>🏨 Hotels Near Windsor & Heathrow</h2>
            <p>
              From boutique hotels in Windsor town centre to large Heathrow airport hotels, we
              provide 24/7 transfers door to door.
            </p>
          </header>
          <div className="windsor-hotels-grid">
            <article className="windsor-hotel-card">
              <h3>Windsor & Eton Hotels</h3>
              <p>
                Town-centre accommodation near Windsor Castle and the river – perfect for short
                breaks.
              </p>
              <ul>
                <li>📍 Short taxi hops around Windsor & Eton</li>
                <li>🛫 Easy connections to Heathrow terminals</li>
                <li>💷 Typical Windsor → Heathrow fare: £40–£55</li>
              </ul>
            </article>
            <article className="windsor-hotel-card">
              <h3>Heathrow Perimeter Hotels</h3>
              <p>
                Large chain hotels around Heathrow’s perimeter roads, used by many Windsor visitors
                before flights.
              </p>
              <ul>
                <li>📍 15–30 minutes from Windsor (traffic dependent)</li>
                <li>💷 Typical fare: £35–£50 (saloon)</li>
                <li>🚐 Minibus upgrades for family or group stays</li>
              </ul>
            </article>
          </div>
          <p className="windsor-hotels-link">
            For a detailed hotel-by-hotel breakdown, visit our{' '}
            <Link to="/heathrow-hotels">Heathrow hotels taxi guide</Link>.
          </p>
        </section>

        {/* FAQ TEASER */}
        <section className="windsor-section" id="windsor-faq">
          <header className="windsor-section-header">
            <h2>❓ Windsor Taxi FAQs</h2>
            <p>
              A few common questions passengers ask when travelling between Windsor, Heathrow and
              other airports.
            </p>
          </header>
          <div className="windsor-faq-grid">
            <article className="windsor-faq-card">
              <h3>Can you collect from Windsor Castle area?</h3>
              <p>
                Yes – pickups around Windsor Castle, town centre hotels and the Long Walk area are
                common. We’ll confirm the best pickup point when you book.
              </p>
            </article>
            <article className="windsor-faq-card">
              <h3>Do you offer taxis from Windsor to Heathrow at night?</h3>
              <p>
                Yes — CheapCabGrab runs 24/7, including early‑morning flights and late‑night
                collections from all Heathrow terminals.
              </p>
            </article>
            <article className="windsor-faq-card">
              <h3>How long is a taxi from Windsor to Heathrow?</h3>
              <p>
                Typically 15–30 minutes depending on route and traffic around the A308/A332 or
                Datchet Road.
              </p>
            </article>
            <article className="windsor-faq-card">
              <h3>Do you provide larger minibuses for Windsor groups?</h3>
              <p>
                Yes — MPV, 7‑seater, 9‑seater and 14‑seater options are available. For large groups,
                see our <Link to="/minibus">minibus</Link> and <Link to="/coach">coach hire</Link>
                routes.
              </p>
            </article>
          </div>

          <p className="windsor-faq-link">
            See all questions in our full <Link to="/faq">CheapCabGrab FAQ guide</Link>.
          </p>
        </section>

        {/* SUPPORT SECTION */}
        <section className="windsor-section" id="windsor-support">
          <header className="windsor-section-header">
            <h2>📞 Need help with a Windsor taxi booking?</h2>
          </header>
          <p>
            Our team is available 24/7 for airport transfers, business travel, local journeys and
            special events around Windsor and Eton.
          </p>
          <p>
            Contact us via our <Link to="/contact">support page</Link> or email
            <strong> support@cheapcabgrab.com</strong>.
          </p>
        </section>

        {/* MOBILE BOTTOM CTA */}
        <div className="route-bottom-cta">
          <button
            className="route-bottom-cta-btn"
            onClick={scrollToForm}
          >
            Book Windsor Taxi
          </button>
        </div>
      </main>

      <FloatingSupportChat
        brand="ccg"
        defaultFlow="enquiry"
        position="right"
        showAfterMs={9000}
        nudgeText="Need Windsor taxi help?"
      />
    </div>



 );          
};            
              
export default WindsorTaxi;
